import { API_BASE_URL } from '@/config/api-url';

/**
 * WEB-1152 (П9) атом 3 — API-клиент публичного портала отмены с удержанием.
 *
 * Типы зеркалят backend/src/public-portal/dto/public-portal.dto.ts
 * (PortalSubscriptionView / PortalCancelResult / PortalDownsellResult).
 * Ручки публичные (без auth, по guid из manage_link), поэтому ходим напрямую
 * через fetch — как publicResolve.ts, а не через авторизованный api-клиент.
 *
 * Чтение подписок и отмена на любую проблему (сеть, не-2xx, кривое тело)
 * отвечают `null` — рендерер портала показывает человекочитаемую ошибку, не
 * падает. Промахи ВЛАДЕНИЯ бэкенд по-прежнему схлопывает в один 404
 * (anti-enum) — различать там нечего.
 *
 * Ревизия 2 WEB-1152 (дефект 1): у скидки исходы РАЗНЫЕ, и схлопывать их в
 * `null` нельзя. Бэкенд отвечает 409 `subscription_cancelling` (подписка уже
 * отменяется — скидку не даём) и 409 `offer_not_configured` (портал не
 * назначен/не опубликован либо цена офферами не предложена) — оба
 * терминальны. Раньше и то, и другое превращалось в «Could not apply the
 * discount. Please try again.», и покупатель жал кнопку бесконечно. Поэтому
 * requestPortalDownsell отдаёт размеченный исход, а блок печатает по нему
 * честный текст и убирает кнопку там, где повтор бессмыслен.
 */

export interface PortalSubscriptionView {
  subscriptionId: string;
  priceId: string | null;
  status: string;
  /** ISO-строка конца оплаченного периода либо null. */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface PortalCancelResult {
  status: 'scheduled_cancel' | 'already_scheduled' | 'already_canceled';
  cancelAtPeriodEnd: true;
  currentPeriodEnd: string | null;
}

export interface PortalDownsellResult {
  status: 'price_changed' | 'already_on_target_price';
  priceId: string;
}

/** WEB-1395: зеркалит PortalResumeResult бэка (public-portal.dto.ts). */
export interface PortalResumeResult {
  status: 'resumed' | 'already_active';
  cancelAtPeriodEnd: false;
  currentPeriodEnd: string | null;
}

/**
 * WEB-1395: исходы возобновления. Терминальные — повтор не поможет:
 *  - `period_ended` — 409 subscription_period_ended (WEB-1256): подписка
 *    терминально отменена (оплаченный период закончился), возобновлять
 *    нечего — только новая покупка;
 *  - `denied` — 404, сессия портала не признана (протухла/личность стёрта).
 * `unavailable` — всё прочее (сеть, 5xx): повторить осмысленно.
 */
export type PortalResumeFailureReason =
  'period_ended' | 'denied' | 'unavailable';

export type PortalResumeOutcome =
  | { ok: true; result: PortalResumeResult }
  | { ok: false; reason: PortalResumeFailureReason };

/**
 * Причины отказа скидки (дефект 1). Терминальные — повтор не поможет:
 *  - `subscription_cancelling` — подписка уже отменяется;
 *  - `offer_not_configured` — портал не назначен/не опубликован либо цена
 *    не предложена блоками downsell_offer портал-пейволла.
 * `unavailable` — всё прочее (сеть, 5xx): повторить осмысленно.
 * FIX-P12-1 (находка 3): `denied` — 404, сессия портала не признана (протухла
 * / личность стёрта) — терминальный исход, отличный от `unavailable`: ведёт
 * к повторному входу, а не к бессмысленному повтору кнопки.
 * WEB-2457 п.4: `downsell_unavailable` — 404 с кодом PUBLIC_FUNNEL_UNAVAILABLE:
 * скидка закрыта тарифом клиента (бесплатный или запертый). Терминальный,
 * но вход НЕ стирается — отмена подписки при этом доступна.
 */
export type PortalDownsellFailureReason =
  | 'subscription_cancelling'
  | 'offer_not_configured'
  | 'downsell_unavailable'
  | 'denied'
  | 'unavailable';

export type PortalDownsellOutcome =
  | { ok: true; result: PortalDownsellResult }
  | { ok: false; reason: PortalDownsellFailureReason };

/**
 * WEB-1192 (п.3): чем страница доказывает право на портальные ручки.
 *  - `{ sessionToken }` — основной путь: непрозрачный токен короткоживущей
 *    сессии (~24 ч), уходит ЗАГОЛОВКОМ `x-portal-session` (не в URL и не в
 *    теле — query попадает в access-логи, ровно тот класс утечки, который
 *    пункт 3 закрывает);
 *  - голая строка guid — легаси-путь текущей загрузки по ?guid= (manage_link)
 *    и окно выката старого бэкенда: уходит параметром/полем, как раньше.
 */
export type PortalAccess = string | { sessionToken: string };

/** Заголовки доступа: токен сессии — только заголовком. */
function accessHeaders(access: PortalAccess): Record<string, string> {
  return typeof access === 'string'
    ? {}
    : { 'x-portal-session': access.sessionToken };
}

/** Поле guid для query/тела: только у легаси-guid-доступа. */
function accessGuidField(access: PortalAccess): Record<string, string> {
  return typeof access === 'string' ? { guid: access } : {};
}

/**
 * GET /public/portal/subscriptions → подписки покупателя.
 * WEB-1192 (п.3): `'denied'` = бэкенд ответил 404 — вход не признан
 * (сессия протухла / личность стёрта). Отличаем от `null` (сеть/5xx):
 * протухшая сессия ведёт на экран «запросите новую ссылку», а не на
 * «попробуйте позже».
 */
export async function fetchPortalSubscriptions(
  projectId: string,
  access: PortalAccess,
): Promise<PortalSubscriptionView[] | null | 'denied'> {
  try {
    // encodeURIComponent, не URLSearchParams: тот кодирует пробел как '+'
    // (форма-кодирование), а контрактный тест клиента закрепляет %20.
    const guidPart =
      typeof access === 'string' ? `&guid=${encodeURIComponent(access)}` : '';
    const url =
      `${API_BASE_URL}/public/portal/subscriptions` +
      `?projectId=${encodeURIComponent(projectId)}${guidPart}`;
    const res = await fetch(url, { headers: accessHeaders(access) });
    if (res.status === 404) return 'denied';
    if (!res.ok) return null;
    const data = (await res.json()) as { subscriptions?: unknown };
    if (!Array.isArray(data?.subscriptions)) return null;
    return data.subscriptions as PortalSubscriptionView[];
  } catch {
    return null;
  }
}

async function postJson<T>(
  path: string,
  body: Record<string, string>,
  headers: Record<string, string> = {},
): Promise<T | null> {
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/**
 * WEB-1152 (примирение атомов): вход по email-ссылке `?code=` на публичном
 * портал-пейволле. GET /public/handoff/portal/resolve?code= → {guid, projectId}
 * (одноразовый portal-токен; бэкенд отвечает единым 404 на просрочен/
 * использован/не найден — anti-enum, различать нечего).
 * null = код невалиден → рендерер показывает «ссылка недействительна».
 */
export async function resolvePortalEntryCode(code: string): Promise<{
  guid: string | null;
  projectId: string;
  /**
   * WEB-1192 (п.3): токен короткоживущей сессии — основной креденшел после
   * входа. null = старый бэкенд (окно выката): работаем по guid, но в
   * адресную строку его всё равно не пишем.
   */
  sessionToken: string | null;
} | null> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/public/handoff/portal/resolve?code=${encodeURIComponent(code)}`,
    );
    if (!res.ok) return null;
    const body = (await res.json()) as {
      data?: { guid?: unknown; projectId?: unknown; sessionToken?: unknown };
    };
    const guid = body?.data?.guid;
    const projectId = body?.data?.projectId;
    const sessionToken = body?.data?.sessionToken;
    if (typeof projectId !== 'string' || !projectId) return null;
    const validGuid = typeof guid === 'string' && guid ? guid : null;
    const validToken =
      typeof sessionToken === 'string' && sessionToken ? sessionToken : null;
    // Без единого креденшела ответ бесполезен — fail-closed.
    if (!validGuid && !validToken) return null;
    return { guid: validGuid, projectId, sessionToken: validToken };
  } catch {
    return null;
  }
}

/**
 * WEB-1192 (п.3): обмен guid из прямой manage_link (?guid=) на токен
 * короткоживущей сессии. POST /public/portal/session {projectId, guid} →
 * {sessionToken}. null = обмен недоступен (старый бэкенд, сеть, 404) —
 * страница доживает на guid в памяти, но URL всё равно чистится.
 */
export async function exchangePortalGuidForSession(
  projectId: string,
  guid: string,
): Promise<string | null> {
  const result = await postJson<{ sessionToken?: unknown }>(
    '/public/portal/session',
    { projectId, guid },
  );
  const token = result?.sessionToken;
  return typeof token === 'string' && token ? token : null;
}

/**
 * Ревизия WEB-1152 (п.5): запрос email-ссылки входа в портал отмены.
 * POST /public/handoff/portal-entry/request {projectId, email} — ручка ВСЕГДА
 * отвечает 204 (anti-enum, существование почты не подтверждается), поэтому
 * возвращаем только «дошёл ли запрос»: true = 204 принят (форма показывает
 * «если такая почта есть — письмо отправлено»), false = сеть/5xx/429.
 */
export async function requestPortalEntryLink(
  projectId: string,
  email: string,
  /**
   * WEB-3412: язык страницы портала — письмо «управление подпиской» уходит на
   * нём. Нет — поле опущено, письмо уйдёт по-английски.
   */
  language?: string,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/public/handoff/portal-entry/request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          email,
          ...(language ? { language } : {}),
        }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * WEB-3413: что портал знает по номеру ссылки «Управлять подпиской» из письма
 * после оплаты (`?entry=`) — ТОЛЬКО маску почты покупки (`s***@gmail.com`).
 * null — номер неизвестен, просрочен, чужой или сбой сети: форма открывается
 * обычной, с полем почты. Номер уходит в теле запроса, не в адресе.
 */
export async function describePortalEntryNumber(
  projectId: string,
  entry: string,
): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/public/handoff/portal-entry/number`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, entry }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { maskedEmail?: unknown } | null;
    const masked = body?.maskedEmail;
    return typeof masked === 'string' && masked ? masked : null;
  } catch {
    return null;
  }
}

/**
 * WEB-3413: попросить письмо входа в портал на почту покупки по номеру из
 * письма. Сервер отвечает 204 всегда (номер неизвестен, просрочен, пауза —
 * письма просто нет); false — только сбой сети или отказ по частоте (429).
 */
export async function requestPortalEntryByNumber(
  projectId: string,
  entry: string,
  /** Язык страницы портала — на нём уйдёт письмо (WEB-3412). */
  language?: string,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/public/handoff/portal-entry/number/request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          entry,
          ...(language ? { language } : {}),
        }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * POST /public/portal/cancel — плановая отмена (cancel_at_period_end).
 * FIX-P12-1 (находка 3): 404 = сессия портала не признана — отдельный исход
 * `'denied'`, симметричный fetchPortalSubscriptions (:103). Раньше схлопывался
 * в `null` («попробуйте позже»), и покупатель со вкладкой старше TTL сессии
 * жал кнопку «Отменить подписку» на неверный текст ошибки.
 */
export async function requestPortalCancel(
  projectId: string,
  access: PortalAccess,
  subscriptionId: string,
): Promise<PortalCancelResult | null | 'denied'> {
  try {
    const res = await fetch(`${API_BASE_URL}/public/portal/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...accessHeaders(access) },
      body: JSON.stringify({
        projectId,
        ...accessGuidField(access),
        subscriptionId,
      }),
    });
    if (res.status === 404) return 'denied';
    if (!res.ok) return null;
    return (await res.json()) as PortalCancelResult;
  } catch {
    return null;
  }
}

/**
 * WEB-1395: POST /public/portal/resume — снятие запланированной отмены
 * (cancel_at_period_end → false). Исходы размечены по образцу downsell:
 * 404 = сессия не признана (`denied`), 409 subscription_period_ended =
 * терминально отменённая подписка (WEB-1256, честный текст без повтора),
 * прочее = временный сбой (`unavailable`).
 */
export async function requestPortalResume(
  projectId: string,
  access: PortalAccess,
  subscriptionId: string,
): Promise<PortalResumeOutcome> {
  try {
    const res = await fetch(`${API_BASE_URL}/public/portal/resume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...accessHeaders(access) },
      body: JSON.stringify({
        projectId,
        ...accessGuidField(access),
        subscriptionId,
      }),
    });
    if (res.ok) {
      const result = (await res.json()) as PortalResumeResult;
      return { ok: true, result };
    }
    if (res.status === 404) {
      return { ok: false, reason: 'denied' };
    }
    if (res.status === 409) {
      const code = await readErrorCode(res);
      if (code === 'subscription_period_ended') {
        return { ok: false, reason: 'period_ended' };
      }
    }
    return { ok: false, reason: 'unavailable' };
  } catch {
    return { ok: false, reason: 'unavailable' };
  }
}

/**
 * Код отказа из тела ответа NestJS (`{ statusCode, message, error }`).
 * 4xx-тела проходят насквозь (AllExceptionsFilter), так что `message` —
 * ровно та строка, что бросил контроллер.
 *
 * WEB-2457 п.4: отказ с кодом реестра (`humanError` на сервере) несёт код в
 * отдельном поле `code`, а в `message` — запасной текст («Not found»). Поле
 * `code` читается первым; у отказов без него (409 портала) — `message`, как
 * раньше.
 */
async function readErrorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { message?: unknown; code?: unknown };
    if (typeof body?.code === 'string' && body.code.length > 0) {
      return body.code;
    }
    const message = body?.message;
    if (typeof message === 'string') return message;
    if (Array.isArray(message) && typeof message[0] === 'string') {
      return message[0];
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * POST /public/portal/downsell — удержание скидкой (swap на дешёвую цену).
 * Ревизия 2 (дефект 1): исходы размечены — блок отличает «уже отменяется» и
 * «оффер не настроен» (повтор не поможет) от временного сбоя.
 */
export async function requestPortalDownsell(
  projectId: string,
  access: PortalAccess,
  subscriptionId: string,
  targetPriceId: string,
): Promise<PortalDownsellOutcome> {
  try {
    const res = await fetch(`${API_BASE_URL}/public/portal/downsell`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...accessHeaders(access) },
      body: JSON.stringify({
        projectId,
        ...accessGuidField(access),
        subscriptionId,
        targetPriceId,
      }),
    });
    if (res.ok) {
      const result = (await res.json()) as PortalDownsellResult;
      return { ok: true, result };
    }
    // FIX-P12-1 (находка 3): 404 = сессия портала не признана — отдельный
    // терминальный исход 'denied', раньше схлопывался в 'unavailable'.
    // WEB-2457 п.4: кроме одного случая — скидка закрыта тарифом клиента
    // (бесплатный или запертый): 404 с кодом PUBLIC_FUNNEL_UNAVAILABLE.
    // Сессия при этом жива, стирать вход нельзя — конечный исход «скидка
    // недоступна», покупатель остаётся на портале и может отменить.
    if (res.status === 404) {
      const code = await readErrorCode(res);
      if (code === 'PUBLIC_FUNNEL_UNAVAILABLE') {
        return { ok: false, reason: 'downsell_unavailable' };
      }
      return { ok: false, reason: 'denied' };
    }
    if (res.status === 409) {
      const code = await readErrorCode(res);
      if (code === 'subscription_cancelling') {
        return { ok: false, reason: 'subscription_cancelling' };
      }
      if (code === 'offer_not_configured') {
        return { ok: false, reason: 'offer_not_configured' };
      }
    }
    return { ok: false, reason: 'unavailable' };
  } catch {
    return { ok: false, reason: 'unavailable' };
  }
}
